import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Network, Users, Building2, ChevronDown, ChevronRight,
  ZoomIn, ZoomOut, GitBranch, List, Copy
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente TreeNode para la vista de árbol
function TreeNode({ cargo, areas, personas, cargos, expanded, onToggle, onSelect, selectedId }) {
  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nombre : '-';
  };

  const getPersonasByCargo = (cargoId) => {
    return personas.filter(p => p.cargo_id === cargoId);
  };

  const getSubCargos = (cargoId) => {
    return cargos.filter(c => c.cargo_superior_id === cargoId);
  };

  const subCargos = getSubCargos(cargo.id);
  const cargoPersonas = getPersonasByCargo(cargo.id);
  const hasChildren = subCargos.length > 0;
  const isExpanded = expanded[cargo.id] === true;
  const isSelected = selectedId === cargo.id;

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`relative bg-white border-2 rounded-xl p-3 min-w-[180px] max-w-[200px] cursor-pointer hover:border-teal-400 transition-all ${isSelected ? 'border-teal-500 shadow-lg' : 'border-slate-200'}`}
        onClick={() => onSelect(cargo)}
        data-testid={`portal-tree-node-${cargo.id}`}
      >
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-teal-500 text-white text-[10px] font-medium rounded-full">
          Nivel {cargo.nivel}
        </div>
        
        <div className="flex items-start gap-2 mt-1">
          <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Network className="w-4 h-4 text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-900 truncate">{cargo.nombre}</p>
            <p className="text-[10px] text-slate-500 truncate">{getAreaName(cargo.area_id)}</p>
            {cargoPersonas.length > 0 && (
              <div className="mt-1">
                {cargoPersonas.slice(0, 2).map((p, idx) => (
                  <p key={idx} className="text-[10px] text-teal-600 font-medium truncate">
                    {p.nombre} {p.apellido}
                  </p>
                ))}
                {cargoPersonas.length > 2 && (
                  <p className="text-[10px] text-slate-400">+{cargoPersonas.length - 2} más</p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {hasChildren && (
          <button
            className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-slate-100 border border-slate-300 rounded-full flex items-center justify-center hover:bg-slate-200"
            onClick={(e) => { e.stopPropagation(); onToggle(cargo.id); }}
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div className="flex flex-col items-center">
          <div className="w-0.5 h-6 bg-slate-300"></div>
          <div className="flex gap-4">
            {subCargos.map((sub) => (
              <div key={sub.id} className="flex flex-col items-center">
                <div className="w-0.5 h-4 bg-slate-300"></div>
                <TreeNode
                  cargo={sub}
                  areas={areas}
                  personas={personas}
                  cargos={cargos}
                  expanded={expanded}
                  onToggle={onToggle}
                  onSelect={onSelect}
                  selectedId={selectedId}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const PortalOrganigrama = () => {
  const [cargos, setCargos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [viewMode, setViewMode] = useState('tree');
  const [zoom, setZoom] = useState(100);
  const [selectedCargo, setSelectedCargo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/trabajador/organigrama`);
      const data = res.data;
      
      setCargos(data.cargos || []);
      setAreas(data.areas || []);
      setPersonas(data.personas || []);
      
      const exp = {};
      (data.cargos || []).forEach((c) => { exp[c.id] = true; });
      setExpanded(exp);
    } catch (err) {
      toast.error('Error al cargar el organigrama');
    } finally {
      setLoading(false);
    }
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nombre : '-';
  };

  const getPersonasByCargo = (cargoId) => {
    return personas.filter(p => p.cargo_id === cargoId);
  };

  const getSubCargos = (cargoId) => {
    return cargos.filter(c => c.cargo_superior_id === cargoId);
  };

  const getRootCargos = () => {
    return cargos.filter(c => !c.cargo_superior_id);
  };

  const toggleExpand = (cargoId) => {
    setExpanded(prev => ({
      ...prev,
      [cargoId]: !prev[cargoId]
    }));
  };

  const handleSelectCargo = (cargo) => {
    setSelectedCargo(cargo);
  };

  const handleZoomIn = () => {
    setZoom(z => Math.min(150, z + 10));
  };

  const handleZoomOut = () => {
    setZoom(z => Math.max(50, z - 10));
  };

  const buildFlatList = () => {
    const result = [];
    const roots = getRootCargos();
    
    const addCargo = (cargo, level) => {
      const subs = getSubCargos(cargo.id);
      result.push({
        cargo: cargo,
        level: level,
        hasChildren: subs.length > 0,
        isExpanded: expanded[cargo.id] === true
      });
      if (expanded[cargo.id]) {
        subs.forEach(sub => addCargo(sub, level + 1));
      }
    };
    
    roots.forEach(root => addCargo(root, 0));
    return result;
  };

  if (loading) {
    return (
      <div className="flex justify-center h-64">
        <div className="animate-spin h-8 w-8 border-b-2 border-teal-500 rounded-full"></div>
      </div>
    );
  }

  const flatList = buildFlatList();
  const rootCargos = getRootCargos();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Organigrama</h1>
          <p className="text-slate-500">Estructura organizacional de la empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${viewMode === 'tree' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600'}`}
              onClick={() => setViewMode('tree')}
              data-testid="portal-view-tree-btn"
            >
              <GitBranch className="w-4 h-4" />
              Árbol
            </button>
            <button
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1 ${viewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600'}`}
              onClick={() => setViewMode('list')}
              data-testid="portal-view-list-btn"
            >
              <List className="w-4 h-4" />
              Lista
            </button>
          </div>
          
          {viewMode === 'tree' && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button className="p-1.5 text-slate-600 hover:text-slate-900 rounded" onClick={handleZoomOut}>
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-600 w-10 text-center">{zoom}%</span>
              <button className="p-1.5 text-slate-600 hover:text-slate-900 rounded" onClick={handleZoomIn}>
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              {viewMode === 'tree' ? (
                <div 
                  className="overflow-x-auto py-8 px-4"
                  style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left' }}
                >
                  <div className="flex flex-col items-center gap-4 min-w-max">
                    {rootCargos.map((cargo) => (
                      <TreeNode
                        key={cargo.id}
                        cargo={cargo}
                        areas={areas}
                        personas={personas}
                        cargos={cargos}
                        expanded={expanded}
                        onToggle={toggleExpand}
                        onSelect={handleSelectCargo}
                        selectedId={selectedCargo ? selectedCargo.id : null}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {flatList.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No hay cargos registrados</p>
                  )}
                  {flatList.map((item) => {
                    const cargo = item.cargo;
                    const cargoPersonas = getPersonasByCargo(cargo.id);
                    const isSelected = selectedCargo && selectedCargo.id === cargo.id;
                    const paddingLeft = item.level * 24 + 12;
                    
                    return (
                      <div
                        key={cargo.id}
                        className={`flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer ${isSelected ? 'bg-teal-50 border-l-4 border-l-teal-500' : ''}`}
                        style={{ paddingLeft: `${paddingLeft}px` }}
                        onClick={() => handleSelectCargo(cargo)}
                        data-testid={`portal-cargo-${cargo.id}`}
                      >
                        {item.hasChildren ? (
                          <button 
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200" 
                            onClick={(e) => { e.stopPropagation(); toggleExpand(cargo.id); }}
                          >
                            {item.isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </button>
                        ) : <div className="w-6"></div>}
                        
                        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Network className="w-4 h-4 text-teal-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{cargo.nombre}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {getAreaName(cargo.area_id)}
                            </span>
                            {cargoPersonas.length > 0 && (
                              <span className="text-teal-600 font-medium flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {cargoPersonas.map(p => `${p.nombre} ${p.apellido}`).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-700">
                          Nivel {cargo.nivel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel de Detalle */}
        <div className="lg:col-span-1">
          <Card className="border border-slate-200 shadow-sm sticky top-20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Network className="w-5 h-5 text-teal-500" />
                Detalle
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCargo ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Cargo</p>
                    <p className="text-lg font-semibold text-slate-900">{selectedCargo.nombre}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Área</p>
                    <p className="text-sm text-slate-700">{getAreaName(selectedCargo.area_id)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Nivel</p>
                    <p className="text-sm text-slate-700">Nivel {selectedCargo.nivel}</p>
                  </div>
                  
                  {getPersonasByCargo(selectedCargo.id).length > 0 ? (
                    <div>
                      <p className="text-xs text-slate-500 uppercase mb-2">
                        <Users className="w-3 h-3 inline mr-1" />
                        Personas en este cargo
                      </p>
                      <div className="space-y-2">
                        {getPersonasByCargo(selectedCargo.id).map((p) => {
                          const copyEmail = (e) => {
                            e.stopPropagation();
                            if (p.email) {
                              navigator.clipboard.writeText(p.email);
                              toast.success('Correo copiado');
                            }
                          };
                          
                          return (
                            <div
                              key={p.id}
                              className="p-2 rounded-lg bg-slate-50"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-white">
                                    {(p.nombre || '')[0]}{(p.apellido || '')[0]}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">
                                    {p.nombre} {p.apellido}
                                  </p>
                                  {p.email && (
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <p className="text-[10px] text-slate-500 truncate flex-1">{p.email}</p>
                                      <button
                                        onClick={copyEmail}
                                        className="p-1 hover:bg-slate-200 rounded flex-shrink-0"
                                        title="Copiar correo"
                                      >
                                        <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-2 bg-slate-50 rounded-lg">
                      Sin personas asignadas
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">
                  Selecciona un cargo para ver el detalle
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Network className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{cargos.length}</p>
              <p className="text-sm text-slate-500">Cargos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{areas.length}</p>
              <p className="text-sm text-slate-500">Áreas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{personas.length}</p>
              <p className="text-sm text-slate-500">Personas</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
